import { useState, useMemo } from 'react'

function AnnonceCard({ annonce, index }) {
  const price = annonce.prix ?? annonce.price
  const surface = annonce.surface
  const rooms = annonce.nb_pieces ?? annonce.rooms
  const city = annonce.ville ?? annonce.city ?? annonce.localisation ?? annonce.location ?? '—'
  const title = annonce.titre ?? annonce.title ?? `Bien ${index + 1}`
  const type = annonce.type_bien ?? annonce.propertyType ?? ''
  const txType = annonce.type_transaction ?? annonce.transactionType ?? ''
  const img = annonce.image ?? annonce.imageUrl
  const agency = annonce.agence ?? annonce.agencyName
  const date = annonce.date_publication ?? annonce.publishedAt
  const url = annonce.url ?? annonce.link ?? annonce.lien ?? annonce.source_url

  const pricePerM2 = price && surface ? Math.round(Number(price) / Number(surface)) : null

  return (
    <div style={{
      background: 'rgb(var(--rgb-brand) / 0.04)',
      border: '1px solid rgb(var(--rgb-brand) / 0.15)',
      borderRadius: 12,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgb(var(--rgb-brand) / 0.4)'}
    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgb(var(--rgb-brand) / 0.15)'}
    >
      {/* Image */}
      <div style={{ height: 120, background: 'rgb(var(--rgb-brand) / 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        {img ? (
          <img src={img} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} />
        ) : (
          <span style={{ fontSize: 36 }}>🏠</span>
        )}
        {txType && (
          <span style={{ position: 'absolute', top: 8, right: 8, background: txType === 'vente' ? 'rgba(0,200,100,0.85)' : 'rgba(0,180,80,0.85)', color: '#fff', borderRadius: 4, fontSize: 10, padding: '2px 7px', fontWeight: 700, textTransform: 'uppercase' }}>
            {txType}
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(var(--rgb-brand) / 0.8)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          {type || 'Bien'}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8, fontSize: 12, color: 'rgb(var(--rgb-ink) / 0.5)' }}>
          <span>📍</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{city}</span>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {surface && (
            <span style={{ fontSize: 11, background: 'rgb(var(--rgb-ink) / 0.06)', padding: '2px 7px', borderRadius: 4, color: 'rgb(var(--rgb-ink) / 0.6)' }}>
              {surface} m²
            </span>
          )}
          {rooms && (
            <span style={{ fontSize: 11, background: 'rgb(var(--rgb-ink) / 0.06)', padding: '2px 7px', borderRadius: 4, color: 'rgb(var(--rgb-ink) / 0.6)' }}>
              {rooms} p.
            </span>
          )}
          {date && (
            <span style={{ fontSize: 11, background: 'rgb(var(--rgb-ink) / 0.06)', padding: '2px 7px', borderRadius: 4, color: 'rgb(var(--rgb-ink) / 0.4)' }}>
              {new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
            </span>
          )}
        </div>

        {/* Prix */}
        {price ? (
          <div style={{ fontSize: 16, fontWeight: 700, color: 'rgb(var(--rgb-brand))' }}>
            {Number(price).toLocaleString('fr-FR')} €
            {pricePerM2 && <span style={{ fontSize: 11, color: 'rgb(var(--rgb-ink) / 0.3)', fontWeight: 400, marginLeft: 6 }}>{pricePerM2.toLocaleString()} €/m²</span>}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'rgb(var(--rgb-ink) / 0.3)' }}>Prix non précisé</div>
        )}

        {agency && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'rgb(var(--rgb-ink) / 0.25)' }}>{agency}</div>
        )}

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, fontSize: 12, color: 'rgb(var(--rgb-brand) / 0.7)', textDecoration: 'none', padding: '5px 10px', borderRadius: 6, background: 'rgb(var(--rgb-brand) / 0.08)', border: '1px solid rgb(var(--rgb-brand) / 0.2)', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgb(var(--rgb-brand) / 0.15)'; e.currentTarget.style.color = 'rgb(var(--rgb-brand))' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgb(var(--rgb-brand) / 0.08)'; e.currentTarget.style.color = 'rgb(var(--rgb-brand) / 0.7)' }}
          >
            Voir l'annonce ↗
          </a>
        )}
      </div>
    </div>
  )
}

const SORT_OPTIONS = [
  { value: '', label: 'Par défaut' },
  { value: 'price_asc', label: 'Prix ↑' },
  { value: 'price_desc', label: 'Prix ↓' },
  { value: 'surface_asc', label: 'Surface ↑' },
  { value: 'surface_desc', label: 'Surface ↓' },
  { value: 'date_desc', label: 'Plus récents' },
  { value: 'date_asc', label: 'Plus anciens' },
]

export function ImmobilierPreviewGrid({ annonces, totalResults, filters }) {
  const [sortBy, setSortBy] = useState('')

  const sortedAnnonces = useMemo(() => {
    if (!sortBy) return annonces
    const getPrice = x => x.prix ?? x.price ?? 0
    const getSurface = x => x.surface ?? 0
    const getDate = x => new Date(x.date_publication ?? x.publishedAt ?? 0)
    return [...annonces].sort((a, b) => {
      if (sortBy === 'price_asc') return getPrice(a) - getPrice(b)
      if (sortBy === 'price_desc') return getPrice(b) - getPrice(a)
      if (sortBy === 'surface_asc') return getSurface(a) - getSurface(b)
      if (sortBy === 'surface_desc') return getSurface(b) - getSurface(a)
      if (sortBy === 'date_asc') return getDate(a) - getDate(b)
      if (sortBy === 'date_desc') return getDate(b) - getDate(a)
      return 0
    })
  }, [annonces, sortBy])

  const activeFilters = filters ? [
    filters.transactionType,
    filters.propertyType,
    filters.location && `📍 ${filters.location}`,
    filters.priceMin && `min ${Number(filters.priceMin).toLocaleString()}€`,
    filters.priceMax && `max ${Number(filters.priceMax).toLocaleString()}€`,
    filters.surfaceMin && `${filters.surfaceMin}m²+`,
    filters.roomsMin && `${filters.roomsMin} pièces+`,
  ].filter(Boolean) : []

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'rgb(var(--rgb-ink) / 0.6)', fontWeight: 500 }}>
          Aperçu — {totalResults?.toLocaleString()} annonce{totalResults > 1 ? 's' : ''} trouvée{totalResults > 1 ? 's' : ''}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ background: 'rgb(var(--rgb-ink) / 0.06)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', borderRadius: 6, padding: '4px 8px', color: 'rgb(var(--rgb-ink) / 0.6)', fontSize: 12, cursor: 'pointer', outline: 'none' }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span style={{ fontSize: 11, color: 'rgb(var(--rgb-brand) / 0.6)', background: 'rgb(var(--rgb-brand) / 0.08)', padding: '3px 10px', borderRadius: 12 }}>
            {annonces.length} affichées
          </span>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {activeFilters.map((f) => (
            <span key={f} style={{ fontSize: 11, color: 'rgb(var(--rgb-brand) / 0.7)', background: 'rgb(var(--rgb-brand) / 0.07)', border: '1px solid rgb(var(--rgb-brand) / 0.18)', borderRadius: 12, padding: '3px 10px' }}>
              {f}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {sortedAnnonces.map((a, i) => <AnnonceCard key={a.id ?? a.reference ?? i} annonce={a} index={i} />)}
      </div>
    </div>
  )
}

export function ImmobilierClaimSelector({ totalResults, previewCount, annonces = [], onClaim, onCancel, disabled, noSearchId }) {
  const max = Math.min(totalResults || annonces.length || 0, 200)
  const [quantity, setQuantity] = useState(Math.min(10, max))
  const creditsNeeded = quantity * 2

  return (
    <div style={{ background: 'rgb(var(--rgb-brand) / 0.06)', border: '1px solid rgb(var(--rgb-brand) / 0.2)', borderRadius: 16, padding: '24px 28px', marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--color-text)' }}>Acquérir ces annonces</h3>
          <p style={{ margin: 0, fontSize: 13, color: 'rgb(var(--rgb-ink) / 0.45)', maxWidth: 380 }}>
            Choisissez le nombre d'annonces à enregistrer dans votre base. Coût : <strong style={{ color: 'rgb(var(--rgb-brand))' }}>2 crédits / annonce</strong>.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'rgb(var(--rgb-ink) / 0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Quantité</label>
              <input
                type="range"
                min="1"
                max={max}
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'rgb(var(--rgb-brand))' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgb(var(--rgb-ink) / 0.25)', marginTop: 2 }}>
                <span>1</span><span style={{ color: 'rgb(var(--rgb-brand))', fontWeight: 600 }}>{quantity}</span><span>{max}</span>
              </div>
            </div>
          </div>

          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgb(var(--rgb-shade) / 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--rgb-ink) / 0.5)' }}>Coût total</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'rgb(var(--rgb-brand))' }}>{creditsNeeded} crédits</span>
          </div>

          {noSearchId && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.25)', fontSize: 12, color: 'rgba(255,200,80,0.9)', marginBottom: 4 }}>
              Relancez la recherche pour activer l'acquisition.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onCancel}
              disabled={disabled}
              style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'rgb(var(--rgb-ink) / 0.05)', border: '1px solid rgb(var(--rgb-ink) / 0.1)', color: 'rgb(var(--rgb-ink) / 0.5)', fontSize: 13, cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              onClick={() => onClaim({ quantity, creditsNeeded })}
              disabled={disabled}
              style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: noSearchId ? 'rgb(var(--rgb-ink) / 0.04)' : 'rgb(var(--rgb-brand) / 0.15)', border: `1px solid ${noSearchId ? 'rgb(var(--rgb-ink) / 0.1)' : 'rgb(var(--rgb-brand) / 0.4)'}`, color: noSearchId ? 'rgb(var(--rgb-ink) / 0.25)' : 'rgb(var(--rgb-brand) / 0.9)', fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              {disabled && !noSearchId ? 'Acquisition...' : `Acquérir ${quantity} annonce${quantity > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
